import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, List, Copy, Check } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useHuntStore } from '@/stores/huntStore';
import { useLocationStore } from '@/stores/locationStore';
import { GpsSignalStrip } from './GpsSignalIndicator';
import { HuntStatus } from '@gps-hunt/shared';

/**
 * Google "G" logo as an SVG icon
 */
const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

/**
 * Global app header with user info, hunt code, GPS signal, and logout
 */
export const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const { displayName, isGuest, signOut } = useAuth();
  const { currentHunt, reset: resetHuntStore } = useHuntStore();
  const gpsSignal = useLocationStore((s) => s.gpsSignal);
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleLogout = async () => {
    try {
      resetHuntStore();
      await signOut();
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleCopyCode = async () => {
    if (currentHunt?.joinCode) {
      try {
        await navigator.clipboard.writeText(currentHunt.joinCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <header className="flex-shrink-0 z-50 bg-background/95 backdrop-blur-sm border-b border-surface">
      <div className="flex items-center justify-between px-4 py-2 max-w-4xl mx-auto">
        {/* Logo/Title */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/welcome')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img src="/GPSHuntIcon.png" alt="GPS Hunt" className="w-8 h-8 object-contain" />
            <span className="text-lg font-bold text-text-primary">GPS HUNT</span>
          </button>

          {/* Hunt code badge when in a hunt (hide for completed hunts) */}
          {currentHunt?.joinCode && currentHunt.status !== HuntStatus.COMPLETED && (
            <button
              onClick={handleCopyCode}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/20 hover:bg-primary/30 rounded-lg transition-colors"
              title="Click to copy hunt code"
            >
              <span className="font-mono font-bold text-primary text-sm">
                {currentHunt.joinCode}
              </span>
              {copied ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <Copy className="w-3.5 h-3.5 text-primary/70" />
              )}
            </button>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-surface transition-colors"
          >
            {isGuest ? (
              <User className="w-4 h-4 text-text-secondary" />
            ) : (
              <GoogleIcon className="w-4 h-4" />
            )}
            <span className="text-sm text-text-primary">
              {displayName || 'User'}
              {isGuest && <span className="text-text-muted ml-1">(Guest)</span>}
            </span>
          </button>

          {/* Dropdown menu */}
          {showMenu && (
            <>
              {/* Backdrop to close menu */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowMenu(false)} 
              />
              
              <div className="absolute right-0 top-full mt-1 z-50 bg-surface rounded-lg shadow-lg border border-surface-light min-w-[160px] py-1">
                {!isGuest && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      navigate('/my-hunts');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-text-primary hover:bg-surface-light transition-colors"
                  >
                    <List className="w-4 h-4" />
                    <span>My Hunts</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-red-400 hover:bg-surface-light transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Log Out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* GPS Status row */}
      {gpsSignal.isWatching && (
        <div className="flex items-center justify-between px-4 py-1 text-xs">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${
              gpsSignal.isLoading ? 'bg-yellow-500 animate-pulse' :
              gpsSignal.error ? 'bg-red-500' :
              'bg-green-500'
            }`} />
            <span className="text-text-secondary">
              {gpsSignal.isLoading ? 'Acquiring...' :
               gpsSignal.error ? 'Error' :
               'Connected'}
            </span>
          </div>
          {gpsSignal.accuracy !== null && (
            <span className="text-text-muted font-mono">
              ± {gpsSignal.accuracy.toFixed(0)}m
            </span>
          )}
        </div>
      )}

      {/* GPS Signal Strip at bottom edge */}
      <GpsSignalStrip
        accuracy={gpsSignal.accuracy}
        isLoading={gpsSignal.isLoading}
        isWatching={gpsSignal.isWatching}
      />
    </header>
  );
};

export default AppHeader;
